import { Controller } from "@hotwired/stimulus"

// Owns the entire Calendly-style "Weekly available hours" grid.
// State shape: { sun: [], mon: [{start: "09:00", end: "17:00"}, ...], ... }.
// Empty array = day unavailable. Multiple blocks per day = split shifts.
// Serializes the full state into a single hidden JSON input on every change
// so the form submit stays a one-trip POST without nested-array-index hell.
export default class extends Controller {
  static targets = ["rows", "jsonInput"]
  static values = {
    initial: Object,
    dayKeys: Array,
    dayLabels: Object,
    dayLabelsFull: Object,
    maxBlocks: { type: Number, default: 6 },
  }

  connect() {
    this.state = this._normalizeInitial(this.initialValue)
    this.openCopyDay = null
    this.copySelection = new Set()
    this._render()
    this._boundOutsideClick = (e) => this._maybeCloseCopy(e)
    document.addEventListener("click", this._boundOutsideClick)
    // The stylesheet stacks a day row differently by viewport (see
    // _syncReadingOrder), so a resize across the breakpoint re-orders the DOM.
    this._boundResize = () => this._syncReadingOrder()
    window.addEventListener("resize", this._boundResize)
  }

  disconnect() {
    document.removeEventListener("click", this._boundOutsideClick)
    window.removeEventListener("resize", this._boundResize)
  }

  // ---- State + rendering ----

  _normalizeInitial(input) {
    const state = {}
    const days = this.dayKeysValue
    days.forEach((d) => { state[d] = [] })
    if (input && typeof input === "object") {
      days.forEach((d) => {
        const v = input[d]
        if (Array.isArray(v)) {
          state[d] = v
            .filter((b) => b && typeof b === "object")
            .map((b) => ({ start: String(b.start || "09:00"), end: String(b.end || "17:00") }))
        }
      })
    }
    return state
  }

  _render() {
    this.rowsTarget.innerHTML = ""
    this.dayKeysValue.forEach((day) => this._renderDay(day))
    this._syncReadingOrder()
    this._syncJson()
  }

  // A11Y-2 (#13361): Tab order follows DOM order, but the row's PAINTED order
  // depends on the viewport — _calendar_availability.scss lays a day row out as
  // one line on desktop (label | times | actions) and, below the mobile
  // breakpoint, as two grid areas ('header actions' over 'body body'), where
  // the action buttons sit on the line ABOVE the time inputs. Built in
  // header → body → actions order, the mobile row Tabbed from the end-time
  // input back UP to "Add another time block" (the audit's A11Y-2 line on
  // every day). The grid places the three cells by named area, so their DOM
  // order is free: after every render, and on resize, each row is measured
  // and the actions cell is placed before the body whenever it paints wholly
  // above it, and after it when the two share a line. Reading the painted
  // boxes — not a duplicated breakpoint literal — keeps this true for
  // whatever the stylesheet does; an unpainted row (jsdom, display: none) is
  // left alone.
  //
  // A row that holds focus is reconciled too (#13590): moving an element
  // that contains document.activeElement blurs it and drops the caret, so the
  // cell that is moved is always the sibling WITHOUT focus — the actions cell
  // around a focused time input, the body around a focused action button —
  // which lands the same order while the focused control, its value and its
  // selection stay exactly where they were. So the next Tab after a resize
  // follows the painted order without an add / remove / toggle in between.
  _syncReadingOrder() {
    this.rowsTarget.querySelectorAll(".gl-day-row").forEach((row) => {
      const body = row.querySelector(".gl-day-body")
      const actions = row.querySelector(".gl-day-actions")
      if (!body || !actions || !actions.firstElementChild) return

      const bodyBox = body.getBoundingClientRect()
      const actionsBox = actions.getBoundingClientRect()
      if (!bodyBox.height || !actionsBox.height) return

      const actionsAbove = actionsBox.bottom <= bodyBox.top
      const actionsFirst = Boolean(actions.compareDocumentPosition(body) & Node.DOCUMENT_POSITION_FOLLOWING)
      if (actionsAbove === actionsFirst) return

      this._placeSiblings(body, actions, actionsAbove ? actions : body)
    })
  }

  // Put `first` (one of body/actions) directly before the other by moving the
  // cell that does not contain the focused element; both orders leave the
  // header cell where it is.
  _placeSiblings(body, actions, first) {
    const second = first === body ? actions : body
    const focusInFirst = first.contains(document.activeElement)
    if (focusInFirst) second.parentElement.insertBefore(second, first.nextSibling)
    else first.parentElement.insertBefore(first, second)
  }

  _renderDay(day) {
    const blocks = this.state[day] || []

    if (blocks.length === 0) {
      this.rowsTarget.appendChild(this._buildDayRow(day, null, 0, true, true))
      return
    }

    blocks.forEach((block, idx) => {
      const isFirst = idx === 0
      const isLast = idx === blocks.length - 1
      this.rowsTarget.appendChild(this._buildDayRow(day, block, idx, isFirst, isLast))
    })
  }

  _buildDayRow(day, block, idx, isFirst, isLast) {
    const row = document.createElement("div")
    row.className = "gl-day-row"
    row.dataset.day = day
    row.dataset.index = idx
    if (block === null) row.classList.add("is-disabled")

    row.appendChild(this._buildHeader(day, isFirst))
    row.appendChild(this._buildBody(day, block, idx))
    row.appendChild(this._buildActions(day, idx, isFirst, isLast, block !== null))
    return row
  }

  _buildHeader(day, isFirst) {
    const header = document.createElement("div")
    header.className = "gl-day-header"

    if (!isFirst) {
      header.classList.add("is-spacer")
      return header
    }

    const enabled = (this.state[day] || []).length > 0
    const cb = document.createElement("input")
    cb.type = "checkbox"
    cb.className = "form-check-input gl-day-checkbox"
    cb.id = `gl_day_${day}`
    cb.checked = enabled
    cb.dataset.day = day
    cb.dataset.action = "change->calendar-availability#toggleDay"

    const label = document.createElement("label")
    label.className = "gl-day-label"
    label.htmlFor = cb.id
    label.textContent = this.dayLabelsValue[day] || day

    header.append(cb, label)
    return header
  }

  _buildBody(day, block, idx) {
    const body = document.createElement("div")
    body.className = "gl-day-body"

    if (block === null) {
      const span = document.createElement("span")
      span.className = "gl-unavailable-label"
      span.textContent = "Unavailable"
      body.appendChild(span)
      return body
    }

    body.appendChild(this._buildTimeInput(day, idx, "start", block.start))

    const sep = document.createElement("span")
    sep.className = "gl-time-sep"
    sep.textContent = "To"
    body.appendChild(sep)

    body.appendChild(this._buildTimeInput(day, idx, "end", block.end))
    return body
  }

  _buildTimeInput(day, idx, kind, value) {
    const input = document.createElement("input")
    input.type = "time"
    input.className = "gl-form-control gl-time-input"
    input.value = value
    input.dataset.day = day
    input.dataset.index = idx
    input.dataset.kind = kind
    input.dataset.action = "input->calendar-availability#updateTime"
    // FORM-1 / A11Y-1 (#13361): the day checkbox's label names the ROW, not the
    // field — each time input names itself ("Monday start time", "Monday end
    // time", "Monday block 2 start time"), with an id unique per (day, block, edge).
    input.id = `gl_time_${day}_${idx}_${kind}`
    input.setAttribute("aria-label", this._timeInputLabel(day, idx, kind))
    return input
  }

  _timeInputLabel(day, idx, kind) {
    const dayName = this.dayLabelsFullValue[day] || this.dayLabelsValue[day] || day
    const block = idx > 0 ? ` block ${idx + 1}` : ""
    return `${dayName}${block} ${kind} time`
  }

  _buildActions(day, idx, isFirst, isLast, hasBlock) {
    const actions = document.createElement("div")
    actions.className = "gl-day-actions"
    if (!hasBlock) return actions

    // Always render 3 slots so the trash icon stays in the same column across
    // first-of-day rows (which show all 3) and subsequent-block rows (which
    // only show trash). Missing slots use an invisible spacer.
    actions.appendChild(isLast ? this._actionButton({
      label: "Add another time block",
      iconClass: "ph-plus",
      cssClass: "gl-action-add",
      action: "calendar-availability#addBlock",
      day, idx,
    }) : this._actionSpacer())

    actions.appendChild(this._actionButton({
      label: "Remove this time block",
      iconClass: "ph-trash",
      cssClass: "gl-action-btn--remove",
      action: "calendar-availability#removeBlock",
      day, idx,
    }))

    actions.appendChild(isLast ? this._actionButton({
      label: "Copy times to other days",
      iconClass: "ph-copy",
      cssClass: "gl-action-copy",
      action: "calendar-availability#openCopy",
      day, idx,
    }) : this._actionSpacer())

    return actions
  }

  _actionSpacer() {
    const span = document.createElement("span")
    span.className = "gl-action-spacer"
    span.setAttribute("aria-hidden", "true")
    return span
  }

  // BUTTON-4 / A11Y-1: an icon-only control carries the shared gl-tooltip pair
  // (data-gl-tooltip + aria-label — what gl_tooltip_attrs emits in HAML), never
  // a native title (unthemeable, no keyboard reveal, double-bubbles).
  _actionButton({ label, iconClass, cssClass, action, day, idx }) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = `gl-action-btn ${cssClass}`
    btn.dataset.glTooltip = label
    btn.setAttribute("aria-label", label)
    btn.dataset.day = day
    btn.dataset.index = idx
    btn.dataset.action = action
    btn.innerHTML = `<i class="ph ${iconClass}"></i>`
    return btn
  }

  // ---- Action handlers ----

  toggleDay(event) {
    const day = event.currentTarget.dataset.day
    if (event.currentTarget.checked) {
      const last = this._lastUsedHours()
      this.state[day] = [{ start: last.start, end: last.end }]
    } else {
      this.state[day] = []
    }
    this._render()
  }

  addBlock(event) {
    event.preventDefault()
    const day = event.currentTarget.dataset.day
    const blocks = this.state[day]
    if (blocks.length >= this.maxBlocksValue) return

    const last = blocks[blocks.length - 1]
    const start = this._addMinutes(last?.end || "17:00", 60)
    let end = this._addMinutes(start, 60)
    // _addMinutes clamps overflow back to 23:00, so a last block ending at
    // 22:00 would yield start=23:00 + end=23:00 (zero-duration block that
    // fails model validation on save with no UI hint). Force the end past
    // start so the block is at least minimally viable; the agent can then
    // edit the times before saving.
    if (end <= start) end = "23:59"
    blocks.push({ start, end })
    this._render()
  }

  removeBlock(event) {
    event.preventDefault()
    const day = event.currentTarget.dataset.day
    const idx = parseInt(event.currentTarget.dataset.index, 10)
    this.state[day].splice(idx, 1)
    this._render()
  }

  updateTime(event) {
    const { day, index, kind } = event.currentTarget.dataset
    const i = parseInt(index, 10)
    if (!this.state[day][i]) return
    this.state[day][i][kind] = event.currentTarget.value
    this._syncJson()
  }

  // ---- Copy popover ----

  openCopy(event) {
    event.preventDefault()
    event.stopPropagation()
    const day = event.currentTarget.dataset.day
    this.openCopyDay = day
    this.copySelection = new Set()
    this._closeAllPopovers()
    const popover = this._buildCopyPopover(day)
    event.currentTarget.parentElement.appendChild(popover)
  }

  _closeAllPopovers() {
    this.element.querySelectorAll(".gl-copy-popover").forEach((el) => el.remove())
  }

  _maybeCloseCopy(event) {
    if (!this.openCopyDay) return
    const inside = event.target.closest(".gl-copy-popover, .gl-action-copy")
    if (!inside) {
      this._closeAllPopovers()
      this.openCopyDay = null
    }
  }

  toggleCopyAll(event) {
    const checked = event.currentTarget.checked
    const popover = event.currentTarget.closest(".gl-copy-popover")
    popover.querySelectorAll('[data-day-checkbox="true"]').forEach((cb) => {
      if (cb.disabled) return
      cb.checked = checked
      const d = cb.dataset.day
      if (checked) this.copySelection.add(d)
      else this.copySelection.delete(d)
    })
  }

  toggleCopyDay(event) {
    const day = event.currentTarget.dataset.day
    if (event.currentTarget.checked) this.copySelection.add(day)
    else this.copySelection.delete(day)
    const popover = event.currentTarget.closest(".gl-copy-popover")
    const allCb = popover.querySelector('[data-copy-all="true"]')
    if (allCb) {
      const enabledCbs = popover.querySelectorAll('[data-day-checkbox="true"]:not(:disabled)')
      const allChecked = Array.from(enabledCbs).every((cb) => cb.checked)
      allCb.checked = allChecked
    }
  }

  applyCopy(event) {
    event.preventDefault()
    if (!this.openCopyDay) return
    const sourceBlocks = this.state[this.openCopyDay] || []
    const cloned = JSON.parse(JSON.stringify(sourceBlocks))
    this.copySelection.forEach((day) => {
      if (day === this.openCopyDay) return
      this.state[day] = JSON.parse(JSON.stringify(cloned))
    })
    this._closeAllPopovers()
    this.openCopyDay = null
    this._render()
  }

  cancelCopy(event) {
    event.preventDefault()
    this._closeAllPopovers()
    this.openCopyDay = null
  }

  _buildCopyPopover(sourceDay) {
    const pop = document.createElement("div")
    pop.className = "gl-copy-popover"
    pop.addEventListener("click", (e) => e.stopPropagation())

    const title = document.createElement("div")
    title.className = "gl-copy-popover-title"
    title.textContent = "Copy times to..."
    pop.appendChild(title)

    const list = document.createElement("div")
    list.className = "gl-copy-popover-list"
    pop.appendChild(list)

    list.appendChild(this._copyListItem({
      label: "Copy to all",
      isAll: true,
    }))

    this.dayKeysValue.forEach((day) => {
      list.appendChild(this._copyListItem({
        label: this.dayLabelsFullValue[day] || day,
        day,
        disabled: day === sourceDay,
        checked: day === sourceDay,
      }))
    })

    const footer = document.createElement("div")
    footer.className = "gl-copy-popover-footer"
    const apply = document.createElement("button")
    apply.type = "button"
    apply.className = "gl-btn gl-btn-primary gl-btn-sm gl-copy-apply"
    apply.dataset.action = "calendar-availability#applyCopy"
    apply.textContent = "Apply"
    footer.appendChild(apply)
    pop.appendChild(footer)

    return pop
  }

  _copyListItem({ label, day = null, disabled = false, checked = false, isAll = false }) {
    const wrap = document.createElement("label")
    wrap.className = "gl-copy-popover-item" + (disabled ? " is-disabled" : "")

    const cb = document.createElement("input")
    cb.type = "checkbox"
    cb.className = "form-check-input"
    cb.checked = checked
    cb.disabled = disabled
    if (isAll) {
      cb.dataset.copyAll = "true"
      cb.dataset.action = "change->calendar-availability#toggleCopyAll"
    } else {
      cb.dataset.dayCheckbox = "true"
      cb.dataset.day = day
      cb.dataset.action = "change->calendar-availability#toggleCopyDay"
    }

    const span = document.createElement("span")
    span.textContent = label
    wrap.append(cb, span)
    return wrap
  }

  // ---- Helpers ----

  _lastUsedHours() {
    for (const day of this.dayKeysValue) {
      const blocks = this.state[day]
      if (Array.isArray(blocks) && blocks.length > 0) return blocks[0]
    }
    return { start: "09:00", end: "17:00" }
  }

  _addMinutes(hhmm, minutes) {
    const [h, m] = hhmm.split(":").map(Number)
    let total = (h * 60) + m + minutes
    if (total >= 24 * 60) total = (24 * 60) - 60
    if (total < 0) total = 0
    const nh = Math.floor(total / 60)
    const nm = total % 60
    return `${String(nh).padStart(2, "0")}:${String(nm).padStart(2, "0")}`
  }

  _syncJson() {
    this.jsonInputTarget.value = JSON.stringify(this.state)
    // Toggling a day, adding/removing a block, and copy-to-days mutate state from
    // button clicks, which produce no input/change event of their own — notify
    // the surrounding form so card-dirty re-checks. (The connect-time sync fires
    // before card-dirty snapshots and is ignored by its baseline guard.)
    this.jsonInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
